import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ToolType, ToolTypeSearchParams } from '../../services/toolTypeApi';

export interface ToolTypeState {
  items: ToolType[];
  selectedItem: ToolType | null;
  loading: boolean;
  error: string | null;
  validationErrors: string[];
  pagination: { page: number; pageSize: number; total: number };
  searchParams: ToolTypeSearchParams;
  selectedIds: number[];
  bulkLoading: boolean;
  successMessage: string;
}

const initialState: ToolTypeState = {
  items: [], selectedItem: null, loading: false, error: null, validationErrors: [],
  pagination: { page: 1, pageSize: 10, total: 0 },
  searchParams: { page: 1, pageSize: 10, search: '', active: undefined, sortBy: 'Name', sortOrder: 'asc' },
  selectedIds: [], bulkLoading: false, successMessage: '',
};

const toolTypeSlice = createSlice({
  name: 'toolType', initialState,
  reducers: {
    setLoading: (s, a: PayloadAction<boolean>) => { s.loading = a.payload; },
    setError: (s, a: PayloadAction<string | null>) => { s.error = a.payload; },
    setItems: (s, a: PayloadAction<ToolType[]>) => { s.items = a.payload; },
    setSelectedItem: (s, a: PayloadAction<ToolType | null>) => { s.selectedItem = a.payload; },
    addItem: (s, a: PayloadAction<ToolType>) => { s.items.unshift(a.payload); },
    updateItem: (s, a: PayloadAction<ToolType>) => { const i = s.items.findIndex(x => x.Id === a.payload.Id); if (i !== -1) s.items[i] = a.payload; if (s.selectedItem?.Id === a.payload.Id) s.selectedItem = a.payload; },
    removeItem: (s, a: PayloadAction<number>) => { s.items = s.items.filter(x => x.Id !== a.payload); if (s.selectedItem?.Id === a.payload) s.selectedItem = null; },
    setValidationErrors: (s, a: PayloadAction<string[]>) => { s.validationErrors = a.payload; },
    clearValidationErrors: (s) => { s.validationErrors = []; },
    setPagination: (s, a: PayloadAction<Partial<ToolTypeState['pagination']>>) => { s.pagination = { ...s.pagination, ...a.payload } as any; },
    setSearchParams: (s, a: PayloadAction<Partial<ToolTypeSearchParams>>) => { s.searchParams = { ...s.searchParams, ...a.payload } as any; },
    setSelectedIds: (s, a: PayloadAction<number[]>) => { s.selectedIds = a.payload; },
    addSelectedId: (s, a: PayloadAction<number>) => { if (!s.selectedIds.includes(a.payload)) s.selectedIds.push(a.payload); },
    removeSelectedId: (s, a: PayloadAction<number>) => { s.selectedIds = s.selectedIds.filter(id => id !== a.payload); },
    clearSelectedIds: (s) => { s.selectedIds = []; },
    setBulkLoading: (s, a: PayloadAction<boolean>) => { s.bulkLoading = a.payload; },
    setSuccessMessage: (s, a: PayloadAction<string>) => { s.successMessage = a.payload; },
    clearSuccessMessage: (s) => { s.successMessage = ''; },
    resetToolTypeState: () => initialState,
  }
});

export const {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setValidationErrors, clearValidationErrors,
  setPagination, setSearchParams,
  setSelectedIds, addSelectedId, removeSelectedId, clearSelectedIds,
  setBulkLoading, setSuccessMessage, clearSuccessMessage, resetToolTypeState,
} = toolTypeSlice.actions;

export const FETCH_TOOLTYPE = 'toolType/fetch';
export const FETCH_TOOLTYPE_BY_ID = 'toolType/fetchById';
export const CREATE_TOOLTYPE = 'toolType/create';
export const UPDATE_TOOLTYPE = 'toolType/update';
export const DELETE_TOOLTYPE = 'toolType/delete';
export const SEARCH_TOOLTYPE = 'toolType/search';
export const BULK_ACTIVATE_TOOLTYPE = 'toolType/bulkActivate';
export const BULK_DEACTIVATE_TOOLTYPE = 'toolType/bulkDeactivate';
export const BULK_DELETE_TOOLTYPE = 'toolType/bulkDelete';
export const FETCH_TOOLTYPE_STATS = 'toolType/fetchStats';
export const EXPORT_TOOLTYPE_CSV = 'toolType/exportCsv';

export const fetchToolType = (p?: ToolTypeSearchParams) => ({ type: FETCH_TOOLTYPE, payload: p });
export const fetchToolTypeById = (id: number) => ({ type: FETCH_TOOLTYPE_BY_ID, payload: id });
export const createToolType = (item: Omit<ToolType, 'Id'>) => ({ type: CREATE_TOOLTYPE, payload: item });
export const updateToolType = (id: number, item: Partial<ToolType>) => ({ type: UPDATE_TOOLTYPE, payload: { id, item } });
export const deleteToolType = (id: number) => ({ type: DELETE_TOOLTYPE, payload: id });
export const searchToolType = (p: ToolTypeSearchParams) => ({ type: SEARCH_TOOLTYPE, payload: p });
export const bulkActivateToolType = (ids: number[]) => ({ type: BULK_ACTIVATE_TOOLTYPE, payload: ids });
export const bulkDeactivateToolType = (ids: number[]) => ({ type: BULK_DEACTIVATE_TOOLTYPE, payload: ids });
export const bulkDeleteToolType = (ids: number[]) => ({ type: BULK_DELETE_TOOLTYPE, payload: ids });
export const fetchToolTypeStats = () => ({ type: FETCH_TOOLTYPE_STATS });
export const exportToolTypeCsv = (p?: ToolTypeSearchParams) => ({ type: EXPORT_TOOLTYPE_CSV, payload: p });

export default toolTypeSlice.reducer; 